import forge from "node-forge";

export interface HybridEncrypted {
  encryptedMessage: string;
  encryptedKey: string;
  iv: string;
}

export interface GroupMessageEncryptedType {
  cipherText: string;
  iv: string;
  tag: string;
}

const EncryptDecryptService = new (class EncryptDecryptService {
  encryptWithPublicKey = (
    message: string,
    publicKeyPem: string
  ): string | null => {
    try {
      // 1. Validate inputs
      if (!message || message.trim() === "") {
        throw new Error("Message must be a non-empty string");
      }

      if (!publicKeyPem.includes("BEGIN PUBLIC KEY")) {
        throw new Error("Invalid public key format");
      }

      // 2. Parse the public key
      const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);

      // 3. Encrypt using RSA-OAEP
      const encryptedBytes = publicKey.encrypt(message, "RSA-OAEP");

      // 4. Convert to Base64
      const encryptedBase64 = forge.util.encode64(encryptedBytes);

      return encryptedBase64;
    } catch (err) {
      console.error("Encryption failed:", err);
      //throw new Error("Message encryption failed");
      return null;
    }
  };

  decryptWithPublicKey = (
    message: string,
    myPrivateKey: string
  ): string | null => {
    try {
      // Validate inputs
      if (!message || message.trim() === "") {
        throw new Error("Message must be a non-empty string");
      }
      if (!myPrivateKey.includes("BEGIN PRIVATE KEY")) {
        throw new Error("Invalid private key format");
      }

      // Parse the private key from PEM format
      const privateKey = forge.pki.privateKeyFromPem(myPrivateKey);

      // Decode the message from base64
      const encryptedBytes = forge.util.decode64(message);

      // Decrypt using RSA-OAEP
      const decrypted = privateKey.decrypt(encryptedBytes, "RSA-OAEP");

      return decrypted;
    } catch (err) {
      console.error("Decryption failed:", err);
      //throw new Error("Message decryption failed");
      return null;
    }
  };

  hybridEncrypt = (
    message: string,
    publicKeyPem: string
  ): HybridEncrypted | null => {
    try {
      if (!message || message.trim() === "") {
        throw new Error("Message must be a non-empty string");
      }

      if (!publicKeyPem.includes("BEGIN PUBLIC KEY")) {
        throw new Error("Invalid public key format");
      }

      // 1. Parse RSA public key
      const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);

      // 2. Generate random AES key (256-bit) and IV (16 bytes)
      const aesKey = forge.random.getBytesSync(32); // 32 bytes = 256 bits
      const iv = forge.random.getBytesSync(16); // 16 bytes = 128-bit block size

      // 3. Encrypt message with AES-CBC
      const cipher = forge.cipher.createCipher("AES-CBC", aesKey);
      cipher.start({ iv });
      cipher.update(forge.util.createBuffer(message, "utf8"));
      cipher.finish();
      const encryptedMessage = cipher.output.getBytes();

      // 4. Encrypt AES key with RSA-OAEP
      const encryptedKey = publicKey.encrypt(aesKey, "RSA-OAEP");

      // 5. Convert all outputs to Base64
      return {
        encryptedMessage: forge.util.encode64(encryptedMessage),
        encryptedKey: forge.util.encode64(encryptedKey),
        iv: forge.util.encode64(iv),
      };
    } catch (err) {
      console.error("Hybrid encryption failed:", err);
      //throw new Error("Hybrid encryption failed");
      return null;
    }
  };

  hybridDecrypt = (
    encryptedData: {
      encryptedMessage: string;
      encryptedKey: string;
      iv: string;
    },
    privateKeyPem: string
  ): string | null => {
    try {
      if (!privateKeyPem.includes("BEGIN PRIVATE KEY")) {
        throw new Error("Invalid private key format");
      }

      // 1. Parse RSA private key
      const privateKey = forge.pki.privateKeyFromPem(privateKeyPem);

      // 2. Decrypt AES key using RSA-OAEP
      const encryptedAesKeyBytes = forge.util.decode64(
        encryptedData.encryptedKey
      );
      const aesKey = privateKey.decrypt(encryptedAesKeyBytes, "RSA-OAEP");

      // 3. Decode IV and AES-encrypted message from Base64
      const ivBytes = forge.util.decode64(encryptedData.iv);
      const encryptedMessageBytes = forge.util.decode64(
        encryptedData.encryptedMessage
      );

      // 4. Decrypt message using AES-CBC
      const decipher = forge.cipher.createDecipher("AES-CBC", aesKey);
      decipher.start({ iv: ivBytes });
      decipher.update(forge.util.createBuffer(encryptedMessageBytes));
      const success = decipher.finish();

      if (!success) {
        throw new Error("AES decryption failed");
      }

      // 5. Return decrypted message as UTF-8 string
      //return decipher.output.toString("utf8");

      // Convert to UTF-8 string
      const outputBuffer = decipher.output;
      const decryptedMessage = forge.util.decodeUtf8(outputBuffer.getBytes());

      return decryptedMessage;
    } catch (err) {
      console.error("Hybrid decryption failed:", err);
      //throw new Error("Hybrid decryption failed");
      return null;
    }
  };

  encryptGroup = async (
    secretKeyBase64: string,
    plainText: string
  ): Promise<GroupMessageEncryptedType | null> => {
    try {
      // Convert Base64 key → ArrayBuffer
      const keyRaw = Uint8Array.from(atob(secretKeyBase64), (c) =>
        c.charCodeAt(0)
      );

      // Import key for AES-GCM
      const cryptoKey = await window.crypto.subtle.importKey(
        "raw",
        keyRaw,
        "AES-GCM",
        false,
        ["encrypt"]
      );

      // Generate 12-byte IV (recommended size for GCM)
      const iv = window.crypto.getRandomValues(new Uint8Array(12));

      // Encrypt
      const encrypted = await window.crypto.subtle.encrypt(
        {
          name: "AES-GCM",
          iv,
          tagLength: 128,
        },
        cryptoKey,
        new TextEncoder().encode(plainText)
      );

      // encrypted = cipherText + tag
      const encryptedBytes = new Uint8Array(encrypted);

      // AES-GCM tag is last 16 bytes (128 bits)
      const tag = encryptedBytes.slice(-16);
      const cipherText = encryptedBytes.slice(0, encryptedBytes.length - 16);

      return {
        cipherText: b64encode(cipherText),
        iv: b64encode(iv),
        tag: b64encode(tag),
      };
    } catch (err) {
      console.error("Hybrid decryption failed:", err);
      //throw new Error("Hybrid decryption failed");
      return null;
    }
  };

  decryptGroup = async ({
    secretKey,
    cipherTextBase64,
    ivBase64,
    tagBase64,
  }: {
    secretKey: string; // base64
    cipherTextBase64: string; // base64
    ivBase64: string; // base64
    tagBase64: string; // base64
  }): Promise<string> => {
    // Base64 → Uint8Array
    const keyBytes = Uint8Array.from(atob(secretKey), (c) => c.charCodeAt(0));
    const iv = Uint8Array.from(atob(ivBase64), (c) => c.charCodeAt(0));
    const cipherBytes = Uint8Array.from(atob(cipherTextBase64), (c) =>
      c.charCodeAt(0)
    );
    const tag = Uint8Array.from(atob(tagBase64), (c) => c.charCodeAt(0));

    // Combine cipher + tag (same as Dart code)
    const combined = new Uint8Array(cipherBytes.length + tag.length);
    combined.set(cipherBytes);
    combined.set(tag, cipherBytes.length);

    // Import AES-GCM key
    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      keyBytes,
      { name: "AES-GCM" },
      false,
      ["decrypt"]
    );

    // Decrypt
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv,
        tagLength: 128, // must match Dart
      },
      cryptoKey,
      combined
    );

    // Convert bytes → string
    return new TextDecoder().decode(decryptedBuffer);
  };
})();

export default EncryptDecryptService;

function b64encode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

/* function b64decode(str: string): Uint8Array {
  return Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
}
 */
